import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, Search, Filter,
    RefreshCw, ArrowUpDown, ChevronRight, ChevronsLeft, ChevronsRight, FileText,
    Columns3, Check, Download, X, Clock, Loader2, List
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { CustomDropdown } from '../../components/CustomDropdown ';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import { useClientLevel } from '../../hooks/useClientLevel';

interface FilterConfig {
    key: string;
    label: string;
    type: 'date_range' | 'dropdown' | 'string' | 'multi_select_dropdown';
    field: string;
    options?: { label: string; value?: any; id?: any; selected?: boolean }[];
}

interface ColumnConfig {
    key: string;
    label: string;
    type: string;
    sortable: boolean;
    default: boolean;
    colors?: Record<string, string>;
}

interface ReportConfig {
    report_key: string;
    title: string;
    description: string;
    filters: FilterConfig[];
    quick_filters: { key: string; label: string }[];
    columns: ColumnConfig[];
    formats: string[];
}

const ReportViewer: React.FC = () => {
    const { reportKey } = useParams<{ reportKey: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const level = useClientLevel();

    const [config, setConfig] = useState<ReportConfig | null>(null);
    const [data, setData] = useState<any[]>([]);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // API Parameters State
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
    const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
    const [dateFilter, setDateFilter] = useState<{ field: string; start: string; end: string } | null>(null);
    const [orderBy, setOrderBy] = useState<string>('');
    const [orderDir, setOrderDir] = useState<'ASC' | 'DESC'>('DESC');

    const [searchTerm, setSearchTerm] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);

    // UI State for Panels
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [columnDropdownPos, setColumnDropdownPos] = useState({ top: 0, right: 0 });
    const columnButtonRef = useRef<HTMLButtonElement>(null);

    // AbortController for cancelling export
    const abortControllerRef = useRef<AbortController | null>(null);

    // Pagination from URL
    const limit = parseInt(searchParams.get('limit') || '10');
    const currentPage = parseInt(searchParams.get('page') || '1');
    const offset = (currentPage - 1) * limit;

    const fetchConfig = async (clientIds?: string) => {
        setIsLoadingConfig(config === null);
        try {
            const params: any = {};
            if (clientIds) {
                params.client_id = clientIds;
            } else if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }

            const response = await baseapi.get(`/reports/${reportKey}/config`, { params });
            if (response.data.status) {
                const configData = response.data.data;

                const initialFilters: Record<string, any> = {};
                configData.filters.forEach((filter: any) => {
                    const selectedOptions = filter.options
                        ?.filter((opt: any) => opt.selected)
                        .map((opt: any) => (opt.value ?? opt.id)?.toString());

                    if (selectedOptions && selectedOptions.length > 0) {
                        initialFilters[filter.field] = filter.type === 'multi_select_dropdown'
                            ? selectedOptions
                            : selectedOptions[0];
                    }
                });

                setConfig(configData);
                setActiveFilters(prev => ({ ...prev, ...initialFilters }));
                setSelectedColumns(configData.columns.filter((c: ColumnConfig) => c.default).map((c: ColumnConfig) => c.key));

                if (configData.columns.length > 0 && !orderBy) {
                    const defaultSort = configData.columns.find((c: ColumnConfig) => c.sortable);
                    if (defaultSort) setOrderBy(defaultSort.key);
                }
            }
        } catch (error) {
            showError('Failed to load report configuration');
            if (config === null) navigate('/reports');
        } finally {
            setIsLoadingConfig(false);
        }
    };

    const fetchData = useCallback(async () => {
        if (!reportKey) return;
        setIsLoadingData(true);
        try {
            const payload: any = {
                limit,
                offset,
                timezone: 'Asia/Kolkata'
            };

            if (level.name !== 'global' && level.id) {
                payload.client_id = level.id;
            }

            const cleanFilters = Object.entries(activeFilters).reduce((acc, [key, val]) => {
                if (val !== undefined && val !== null && val !== '') {
                    acc[key] = val;
                }
                return acc;
            }, {} as Record<string, any>);

            if (level.name !== 'global' && level.id) {
                cleanFilters.client_id = level.id;
            }

            if (Object.keys(cleanFilters).length > 0) payload.filters = JSON.stringify(cleanFilters);
            if (activeQuickFilter) payload.quick_filter = activeQuickFilter;
            if (dateFilter) payload.date_filter = JSON.stringify(dateFilter);
            if (orderBy) payload.order_by = orderBy;
            if (orderDir) payload.order_dir = orderDir;
            if (searchTerm) payload.search = searchTerm;

            const response = await baseapi.get(`/reports/${reportKey}/data`, { params: payload });
            if (response.data.status) {
                setData(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            }
        } catch (error) {
            showError('Failed to fetch report data');
        } finally {
            setIsLoadingData(false);
        }
    }, [reportKey, limit, offset, activeFilters, activeQuickFilter, dateFilter, orderBy, orderDir, searchTerm, level]);

    const handleExport = async (format: string) => {
        if (!reportKey) return;

        setIsExporting(true);
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const params: any = {
                timezone: 'Asia/Kolkata',
                format: format
            };
            if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }

            const cleanFilters = Object.entries(activeFilters).reduce((acc, [key, val]) => {
                if (val !== undefined && val !== null && val !== '') {
                    acc[key] = val;
                }
                return acc;
            }, {} as Record<string, any>);

            if (level.name !== 'global' && level.id) {
                cleanFilters.client_id = level.id;
            }

            if (Object.keys(cleanFilters).length > 0) params.filters = cleanFilters;
            if (activeQuickFilter) params.quick_filter = activeQuickFilter;
            if (dateFilter) params.date_filter = dateFilter;
            if (orderBy) params.order_by = [{ field: orderBy, direction: orderDir }];
            if (searchTerm) params.search = searchTerm;

            const response = await baseapi.post(`/reports/${reportKey}/export`, params, {
                responseType: 'blob',
                signal: controller.signal
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const extension = format === 'excel' ? 'xlsx' : format;
            const now = new Date();
            const pad = (n: number) => String(n).padStart(2, '0');
            const timestamp = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
            link.setAttribute('download', `${reportKey}_${timestamp}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            if (error.name !== 'CanceledError' && error.message !== 'canceled') {
                showError(`Failed to export ${format.toUpperCase()} report`);
            }
        } finally {
            setIsExporting(false);
            abortControllerRef.current = null;
        }
    };

    const handleCancelExport = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsExporting(false);
    };

    const updatePage = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const updateLimit = (newLimit: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('limit', newLimit.toString());
        params.set('page', '1');
        setSearchParams(params);
    };

    useEffect(() => {
        fetchConfig();
    }, [reportKey, level]);

    useEffect(() => {
        if (config) {
            fetchData();
        }
    }, [fetchData, config]);

    if (isLoadingConfig) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 text-slate-400">
                <div className="relative">
                    <Loader2 className="w-12 h-12 animate-spin text-[#422568]" />
                </div>
                <p className="mt-6 text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Initializing Core Engine</p>
            </div>
        );
    }

    if (!config) return null;

    const handleSort = (columnKey: string) => {
        if (orderBy === columnKey) {
            setOrderDir(orderDir === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setOrderBy(columnKey);
            setOrderDir('DESC');
        }
        updatePage(1);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '--';
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch { return dateStr; }
    };

    const renderCell = (row: any, column: ColumnConfig) => {
        const value = row[column.label] ?? row[column.key];

        if (column.type === 'datetime') {
            return <div className="flex items-center gap-1.5 text-slate-700 font-bold"><Clock size={10} className="text-slate-400" /> {formatDate(value)}</div>;
        }

        const displayValue = typeof value === 'string'
            ? value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : value?.toString() || '--';

        if (column.colors && value && typeof value === 'string') {
            const color = column.colors[value.toLowerCase()];
            if (color) {
                return (
                    <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border" style={{ backgroundColor: `${color}10`, color: color, borderColor: `${color}30` }}>
                        {displayValue}
                    </span>
                );
            }
        }

        return displayValue;
    };

    return (
        <div className="flex flex-col min-h-screen bg-slate-50 text-slate-900 font-sans mb-20">
            {/* Top Navigation */}
            <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-200 sticky top-0 z-[50] shadow-sm">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/reports')} className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all text-slate-600 active:scale-95 group">
                        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-[#422568]" />
                            <h1 className="text-[11px] font-black text-slate-900 uppercase tracking-widest leading-none">{config.title}</h1>
                        </div>
                        <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter mt-0.5">{config.description}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => fetchData()}
                        disabled={isLoadingData}
                        className="p-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-[#422568] hover:border-[#422568] transition-all flex items-center justify-center active:scale-95"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isLoadingData ? 'animate-spin' : ''}`} />
                    </button>
                    <div className="h-5 w-[1px] bg-slate-200 mx-1" />
                    <div className="flex items-center gap-0.5">
                        {config.formats?.map((format) => (
                            <button
                                key={format}
                                onClick={() => handleExport(format)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-slate-50 rounded-lg transition-all active:scale-95"
                                title={`Export ${format.toUpperCase()}`}
                            >
                                <img
                                    src={format.toLowerCase() === 'excel' ? '/xls.png' : `/${format.toLowerCase()}.png`}
                                    alt={format.toUpperCase()}
                                    className="w-4 h-4 object-contain"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col p-4 gap-4">

                {/* Filters and Actions Row */}
                <div className="flex flex-col gap-3">
                    {/* Quick Filters */}
                    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
                        <div className="p-1 bg-slate-200/50 rounded-md mr-1"><List className="w-3 h-3 text-slate-500" /></div>
                        {config.quick_filters.map((qf) => (
                            <button
                                key={qf.key}
                                onClick={() => {
                                    setActiveQuickFilter(activeQuickFilter === qf.key ? null : qf.key);
                                    updatePage(1);
                                }}
                                className={`px-3 py-1 rounded-full text-[9px] font-black border uppercase tracking-widest transition-all ${activeQuickFilter === qf.key
                                    ? 'bg-[#422568] border-[#422568] text-white shadow-lg shadow-[#422568]/20'
                                    : 'bg-white border-slate-200 text-slate-500 hover:border-[#422568] hover:text-[#422568]'
                                    }`}
                            >
                                {qf.label}
                            </button>
                        ))}
                    </div>

                    {/* Search and Secondary Actions */}
                    <div className="flex items-center justify-between gap-3">
                        <div className="relative flex-1 max-w-[320px] group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 group-focus-within:text-[#422568] transition-colors" />
                            <input
                                type="text"
                                placeholder="Quick search..."
                                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-[10px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all placeholder:text-slate-400 uppercase tracking-tight"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    updatePage(1);
                                }}
                            />
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                ref={columnButtonRef}
                                onClick={() => {
                                    if (!showColumnDropdown && columnButtonRef.current) {
                                        const rect = columnButtonRef.current.getBoundingClientRect();
                                        setColumnDropdownPos({ top: rect.bottom, right: window.innerWidth - rect.right });
                                    }
                                    setShowColumnDropdown(!showColumnDropdown);
                                    setShowFilterPanel(false);
                                }}
                                className={`px-4 py-2 rounded-lg border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all ${showColumnDropdown
                                    ? 'bg-[#422568] border-[#422568] text-white shadow-lg shadow-[#422568]/20'
                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                Columns
                            </button>

                            {config.filters.length > 0 && (
                                <button
                                    onClick={() => {
                                        setShowFilterPanel(!showFilterPanel);
                                        setShowColumnDropdown(false);
                                    }}
                                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all border ${showFilterPanel
                                        ? 'bg-[#422568] border-[#422568] text-white shadow-lg shadow-[#422568]/20'
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                        }`}
                                >
                                    <Filter className="w-3.5 h-3.5" />
                                    Filter
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Column Selection Dropdown */}
                {showColumnDropdown && createPortal(
                    <>
                        <div className="fixed inset-0 z-[1000]" onClick={() => setShowColumnDropdown(false)} />
                        <div
                            style={{ position: 'fixed', top: `${columnDropdownPos.top + 8}px`, right: `${columnDropdownPos.right}px` }}
                            className="min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-2xl z-[1001] py-2 animate-in fade-in zoom-in-95 duration-200"
                        >
                            <div className="px-4 py-1.5 border-b border-slate-100 mb-1">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Visibility Control</span>
                            </div>
                            <div className="max-h-64 overflow-y-auto px-1.5 custom-scroll">
                                {config.columns.map((col) => {
                                    const isSelected = selectedColumns.includes(col.key);
                                    return (
                                        <button
                                            key={col.key}
                                            onClick={() => {
                                                if (isSelected) {
                                                    if (selectedColumns.length > 1) setSelectedColumns(selectedColumns.filter(k => k !== col.key));
                                                } else setSelectedColumns([...selectedColumns, col.key]);
                                            }}
                                            className="w-full px-3 py-2 flex items-center justify-between group hover:bg-slate-50 rounded-lg transition-all"
                                        >
                                            <span className={`text-[10px] font-black uppercase tracking-tight transition-colors ${isSelected ? 'text-slate-900' : 'text-slate-400'}`}>
                                                {col.label}
                                            </span>
                                            <div className={`w-3.5 h-3.5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#422568] border-[#422568]' : 'border-slate-200 bg-white'}`}>
                                                {isSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={5} />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>,
                    document.body
                )}

                {/* Filter Panel */}
                {showFilterPanel && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xl animate-in fade-in slide-in-from-top-3 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                            {config.filters
                                .filter(f => level.name === 'global' || f.field !== 'client_id')
                                .map((filter) => (
                                    <div key={filter.key} className="flex flex-col gap-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <div className="w-0.5 h-2.5 bg-[#422568] rounded-full" />
                                            {filter.label?.replace(/_/g, ' ')}
                                        </label>

                                        {filter.type === 'dropdown' ? (
                                            <CustomDropdown
                                                data={filter.options?.map(opt => ({ label: opt.label?.replace(/_/g, ' '), value: (opt.value ?? opt.id)?.toString() ?? '' })) || []}
                                                selectedValue={activeFilters[filter.field]?.toString() || ''}
                                                onSelect={(val) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: val || undefined }));
                                                    updatePage(1);
                                                }}
                                                width="w-full"
                                                placeholder={`Select ${filter.label}`}
                                                buttonHeight={9}
                                            />
                                        ) : filter.type === 'multi_select_dropdown' ? (
                                            <MultiSelectDropdown
                                                options={filter.options?.map(opt => ({ label: opt.label?.replace(/_/g, ' '), value: (opt.value ?? opt.id)?.toString() ?? '' })) || []}
                                                value={Array.isArray(activeFilters[filter.field]) ? activeFilters[filter.field] : []}
                                                onChange={(val) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: val && val.length > 0 ? val : undefined }));
                                                    updatePage(1);
                                                    if (filter.field === 'client_id') fetchConfig(val.join(','));
                                                }}
                                                width="w-full"
                                                placeholder={`Select ${filter.label}`}
                                            />
                                        ) : filter.type === 'date_range' ? (
                                            <div className="flex items-center gap-1.5">
                                                <input
                                                    type="date"
                                                    className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-900 focus:outline-none focus:border-[#422568]"
                                                    value={dateFilter?.field === filter.field ? dateFilter.start : ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setDateFilter(prev => {
                                                            const field = filter.field;
                                                            const end = prev?.field === field ? prev.end : '';
                                                            return val || end ? { field, start: val, end } : null;
                                                        });
                                                        updatePage(1);
                                                    }}
                                                />
                                                <span className="text-slate-300 font-bold">-</span>
                                                <input
                                                    type="date"
                                                    className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-900 focus:outline-none focus:border-[#422568]"
                                                    value={dateFilter?.field === filter.field ? dateFilter.end : ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setDateFilter(prev => {
                                                            const field = filter.field;
                                                            const start = prev?.field === field ? prev.start : '';
                                                            return start || val ? { field, start, end: val } : null;
                                                        });
                                                        updatePage(1);
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder={`Search ${filter.label}...`}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-bold text-slate-900 focus:outline-none focus:border-[#422568]"
                                                value={activeFilters[filter.field] || ''}
                                                onChange={(e) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: e.target.value || undefined }));
                                                    updatePage(1);
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            <div className="flex items-end">
                                {(Object.keys(activeFilters).length > 0 || dateFilter) && (
                                    <button
                                        onClick={() => { setActiveFilters({}); setDateFilter(null); updatePage(1); }}
                                        className="h-8 px-4 text-[9px] font-black text-rose-500 hover:bg-rose-50 rounded-lg uppercase tracking-widest transition-all w-full border border-rose-100"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Table Section */}
                <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-x-auto custom-scroll min-h-0">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-[10]">
                                <tr>
                                    {config.columns.filter(c => selectedColumns.includes(c.key)).map((col, idx) => (
                                        <th
                                            key={col.key}
                                            className={`px-4 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-slate-100 transition-colors' : ''} ${idx === 0 ? 'pl-6' : ''}`}
                                            onClick={() => col.sortable && handleSort(col.key)}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {col.label?.replace(/_/g, ' ')}
                                                {col.sortable && (
                                                    <ArrowUpDown className={`w-3 h-3 ${orderBy === col.key ? 'text-[#422568]' : 'text-slate-300'}`} />
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {isLoadingData && data.length === 0 ? (
                                    <tr>
                                        <td colSpan={selectedColumns.length} className="py-20">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <Loader2 className="w-8 h-8 animate-spin text-[#422568]" />
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Compiling Dataset</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan={selectedColumns.length} className="py-24">
                                            <div className="flex flex-col items-center justify-center gap-3 opacity-40">
                                                <div className="p-3 bg-slate-50 rounded-full"><FileText className="w-10 h-10 text-slate-300" /></div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">No Records Found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-all border-b border-slate-50 last:border-0 group">
                                            {config.columns.filter(c => selectedColumns.includes(c.key)).map((col, cIdx) => (
                                                <td key={col.key} className={`px-4 py-1.5 text-[10px] font-bold text-slate-600 group-hover:text-slate-900 whitespace-nowrap ${cIdx === 0 ? 'pl-6' : ''}`}>
                                                    {renderCell(row, col)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between bg-white shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Show</span>
                                <ItemsPerPageDropdown value={limit} onChange={updateLimit} />
                            </div>
                            <span className="text-[9px] font-black text-slate-400 tracking-widest uppercase">
                                <span className="text-slate-900">{offset + 1}-{Math.min(offset + limit, totalRecords)}</span> <span className="mx-1 opacity-50">of</span> <span className="text-slate-900">{totalRecords}</span> Results
                            </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => updatePage(1)}
                                className="p-1 text-slate-400 hover:text-[#422568] disabled:opacity-20 transition-all bg-slate-50 rounded-md border border-slate-100"
                            >
                                <ChevronsLeft className="w-3.5 h-3.5" />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => updatePage(Math.max(1, currentPage - 1))}
                                className="p-1 text-slate-400 hover:text-[#422568] disabled:opacity-20 transition-all bg-slate-50 rounded-md border border-slate-100"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                            </button>
                            <div className="px-2.5 py-1 bg-[#422568] text-white rounded-md text-[10px] font-black shadow-lg shadow-[#422568]/20 mx-0.5">
                                {currentPage}
                            </div>
                            <button
                                disabled={offset + limit >= totalRecords}
                                onClick={() => updatePage(currentPage + 1)}
                                className="p-1 text-slate-400 hover:text-[#422568] disabled:opacity-20 transition-all bg-slate-50 rounded-md border border-slate-100"
                            >
                                <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                            <button
                                disabled={offset + limit >= totalRecords}
                                onClick={() => updatePage(Math.ceil(totalRecords / limit))}
                                className="p-1 text-slate-400 hover:text-[#422568] disabled:opacity-20 transition-all bg-slate-50 rounded-md border border-slate-100"
                            >
                                <ChevronsRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Export HUD */}
            {isExporting && (
                <div className="fixed inset-0 z-[2000] bg-white/60 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-500">
                    <div className="bg-white border border-slate-200 p-10 rounded-3xl shadow-2xl shadow-[#422568]/10 flex flex-col items-center gap-6 text-center max-w-sm">
                        <div className="relative">
                            <div className="w-20 h-20 border-4 border-[#422568] border-t-[#422568] rounded-full animate-spin" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Download className="w-8 h-8 text-[#422568] animate-bounce" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-1.5">Generating Report</h3>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">
                                Preparing your analytical export.<br /> Please do not close this window.
                            </p>
                        </div>
                        <button
                            onClick={handleCancelExport}
                            className="px-6 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all border border-rose-100 active:scale-95 flex items-center gap-2"
                        >
                            <X className="w-4 h-4" /> Cancel Process
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportViewer;
